import { exportJSON, exportWordDoc, exportToTxtFile } from '../../../helpers/exportTranscript'
import { exportSrt, exportVtt } from '../../../helpers/exportSrtVtt'
import { exportStl } from '../../../helpers/exportStl'
import { apiClient } from "../../../apiClient";
import { DEBUG } from "../../../settings";
import { MAX_CHARS_PER_LINE } from '../../../helpers/subtitleUtil';
import { saveAs } from 'file-saver';
import { CSV_FPS, FORMAT_WORD, FORMAT_RTF } from './exportText/constants';
import { FORMAT_AVID, FORMAT_AVID_EXTENSION, FORMAT_PREMIERE, FORMAT_PREMIERE_EXTENSION } from './exportSubtitles/constants';
import { getExportFilename, exportFile, handleApiError } from '../../../utils/common';

// ********* JSON ********* \\

// Export the transcript as DOC, use the FE version if BE threw error
export function exportAsWordOrRTF(transcript, transcriptStatus, options, footerText, format=FORMAT_WORD) {
    const { includeTimestamps, includeSpeakers, includeHighlights, highlightsOnly, languageCode, filename, stylingFormat } = options;
    const formatForApi = format === FORMAT_WORD ? 'docx' : 'rtf';
    const URL = `/export/${transcriptStatus.data.job.id}/${formatForApi}`;
    const extension = format === FORMAT_WORD ? 'docx' : 'rtf';

    apiClient.get(URL, {
        params: {
            recordId: transcriptStatus.data.recordId,
            includeTimestamps,
            includeSpeakers,
            includeHighlights,
            highlightsOnly,
            footerText,
            languageCode,
            filename,
            ...(stylingFormat ? { stylingFormat } : {}),
        },
        responseType: 'arraybuffer',
    }).then(res => {
        const blob = new Blob([res.data], { type: "application/octet-stream" });
        saveAs(blob, getExportFilename(transcriptStatus.data.audioDisplayFileName, extension));
    }).catch(err => {
        handleApiError(err, 'exportAsWordOrRTF', transcriptStatus.data.recordId, apiClient);
        exportWordDoc(transcript, transcriptStatus, options);
    });
}

// ********* WORD ********* \\

// Export the transcript as DOC, use the FE version if BE threw error
export function exportAsWord(transcript, transcriptStatus, options, footerText) {
    return exportAsWordOrRTF(transcript, transcriptStatus, options, footerText, FORMAT_WORD);
}

// ********* RTF ********* \\

// Export the transcript as RTF, use the FE version if BE threw error
export function exportAsRtf(transcript, transcriptStatus, options, footerText) {
    return exportAsWordOrRTF(transcript, transcriptStatus, options, footerText, FORMAT_RTF);
}

// ********* TEXT ********* \\

// Export the transcript as txt, use the FE version if BE threw error
export function exportAsTxt(transcript, transcriptStatus, options, maxCharsPerRow) {
    const { includeTimestamps, includeSpeakers, highlightsOnly } = options;
    const URL = `/export/${transcriptStatus.data.job.id}/txt`;

    apiClient.get(URL, {
        params: {
            recordId: transcriptStatus.data.recordId,
            maxCharsPerRow: (maxCharsPerRow.active) ? maxCharsPerRow.value : undefined,
            includeTimestamps,
            includeSpeakers,
            highlightsOnly,
        }
    }).then(res => {
        exportFile(getExportFilename(transcriptStatus.data.audioDisplayFileName, 'txt'), res.data, 'text/html;charset=utf-8');
    }).catch(err => {
        handleApiError(err, 'exportAsTxt', transcriptStatus.data.recordId, apiClient);
        exportToTxtFile(transcript, transcriptStatus, options);
    });
}

// ********* JSON ********* \\

// Export the transcript as json, use the FE version if BE threw error
export function exportAsJson(transcript, transcriptStatus, options, isTranscription) {
    const { includeTimestamps, includeSpeakers, highlightsOnly } = options;
    const URL = `/export/${transcriptStatus.data.job.id}/json`;

    apiClient.get(URL, {
        params: {
            recordId: transcriptStatus.data.recordId,
            maxCharsPerRow: MAX_CHARS_PER_LINE,
            includeTimestamps,
            includeSpeakers,
            highlightsOnly,
        }
    }).then(res => {
        const blob = new Blob([JSON.stringify(res.data, null, 4)], { type: "text/html;charset=utf-8" });
        saveAs(blob, getExportFilename(transcriptStatus.data.audioDisplayFileName, 'json'));
    }).catch(err => {
        let errorMessage = 'Error in exportAsJson';
        if (err.response && err.response.data) {
            if (err.response.data.constructor === ArrayBuffer) {
                const decoder = new TextDecoder();
                const dataDecoded = JSON.parse(decoder.decode(err.response.data));
                errorMessage = dataDecoded.message;
            } else {
                errorMessage = err.response.data.message
            }
        }
        apiClient.post('senderroremail', `Error in exportAsJson, recordId: ${transcriptStatus.data.recordId}, errorMessage: ${errorMessage}`, {
            params: {
                errorType: 'subtitleScriptError',
            }
        });
        if (DEBUG) {
            console.log("Error in exportAsJson function: ", `recordId: ${transcriptStatus.data.recordId}, errorMessage: ${errorMessage}`);
        }
        exportJSON(transcript, transcriptStatus, options);
    });
}

// ********* CSV ********* \\

export function exportAsCsv(transcriptStatus, options, maxCharsPerRow) {
    const { frameRate, timeFormat } = options;
    const URL = `/export/${transcriptStatus.data.job.id}/csv`;
  
    const params = {
      timeFormat: timeFormat.toLowerCase(),
    };
  
    if (timeFormat === CSV_FPS) {
      params.frameRate = frameRate;
    }
  
    if (maxCharsPerRow.active) {
      params.maxCharsPerRow = maxCharsPerRow.value;
    }
  
    apiClient
      .get(URL, {
        params,
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
        saveAs(
          blob,
          getExportFilename(transcriptStatus.data.audioDisplayFileName, 'csv'),
        );
      })
      .catch((err) => {
        let errorMessage = 'Error in exportAsCsv';
        if (err.response && err.response.data) {
          if (err.response.data.constructor === ArrayBuffer) {
            const decoder = new TextDecoder();
            const dataDecoded = JSON.parse(decoder.decode(err.response.data));
            errorMessage = dataDecoded.message;
          } else {
            errorMessage = err.response.data.message;
          }
        }
        apiClient.post(
          'senderroremail',
          `Error in exportAsCsv, recordId: ${transcriptStatus.data.recordId}, errorMessage: ${errorMessage}`,
          {
            params: {
              errorType: 'subtitleScriptError',
            },
          },
        );
      });
  }

// ********* SRT ********* \\

export function exportAsSrt(transcript, transcriptStatus, subtitleDurationMax) {
    const URL = `/export/${transcriptStatus.data.job.id}/srt`;
    
    return apiClient.get(URL, {
        params: {
            recordId: transcriptStatus.data.recordId,
            maxCharsPerRow: MAX_CHARS_PER_LINE,
            maxNumberOfRows: 2,
            maxScreenTimePerRowSeconds: subtitleDurationMax,
        }
    }).then(res => {
        const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
        saveAs(blob, getExportFilename(transcriptStatus.data.audioDisplayFileName, 'srt'));
    }).catch(err => {
        handleApiError(err, 'exportAsSrt', transcriptStatus.data.recordId, apiClient);
        exportSrt(transcript.data.segments.valueSeq().toArray(), transcriptStatus, subtitleDurationMax);
    });
}

// ********* VTT ********* \\

export function exportAsVtt(transcript, transcriptStatus, subtitleDurationMax) {
    const URL = `/export/${transcriptStatus.data.job.id}/vtt`;
    
    return apiClient.get(URL, {
        params: {
            recordId: transcriptStatus.data.recordId,
            maxCharsPerRow: MAX_CHARS_PER_LINE,
            maxNumberOfRows: 2,
            maxScreenTimePerRowSeconds: subtitleDurationMax,
        }
    }).then(res => {
        const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
        saveAs(blob, getExportFilename(transcriptStatus.data.audioDisplayFileName, 'vtt'));
    }).catch(err => {
        handleApiError(err, 'exportAsVtt', transcriptStatus.data.recordId, apiClient);
        exportVtt(transcript.data.segments.valueSeq().toArray(), transcriptStatus, subtitleDurationMax);
    });
}

// ********* STL ********* \\

export function exportAsStl(transcript, transcriptStatus, subtitleDurationMax) {
    const URL = `/export/${transcriptStatus.data.job.id}/stl`;
    
    return apiClient.get(URL, {
        params: {
            jobId: transcriptStatus.data.job.id,
            recordId: transcriptStatus.data.recordId,
            maxCharsPerRow: MAX_CHARS_PER_LINE,
            maxNumberOfRows: 2,
            maxScreenTimePerRowSeconds: subtitleDurationMax,
        }
    }).then(res => {      
        const blob = new Blob([res.data], { type: 'application/octet-stream' });
        const exportFileName = getExportFilename(transcriptStatus.data.audioDisplayFileName, 'stl');
        saveAs(blob, exportFileName);
       
    }).catch(err => {
        handleApiError(err, 'exportAsStl', transcriptStatus.data.recordId, apiClient);
        exportStl(transcript, transcriptStatus, subtitleDurationMax);
    });
}

// ********* SUBTITLE SUBTITLES ********* \\
function getFileName(isTranscriber, isTranscription, recordIdOrJobId, transcriptStatus) {
    return isTranscriber && !isTranscription
        ? recordIdOrJobId
        : transcriptStatus.data.audioDisplayFileName;
}
function getFileExtension(subtitleFormat) {
    return subtitleFormat === 'EBU-STL' ? 'stl' : subtitleFormat.toLowerCase();
}

function saveFileContent(data, fileName, fileExtension, contentType = 'text/html;charset=utf-8') {
    const blob = new Blob([data], { type: contentType });
    const exportFileName = getExportFilename(fileName, fileExtension);
    saveAs(blob, exportFileName);
}

function handleStlFormat(res, subtitleFormat, isTranscriber, isTranscription, recordIdOrJobId, transcriptStatus) {
    const fileName = getFileName(isTranscriber, isTranscription, recordIdOrJobId, transcriptStatus);
    const fileExtension = getFileExtension(subtitleFormat);
    
    if (typeof res.data === 'string' && res.data.startsWith('http')) {
        return fetch(res.data)
            .then(response => response.blob())
            .then(blob => {
                const exportFileName = getExportFilename(fileName, fileExtension);
                saveAs(blob, exportFileName);
            })
            .catch(fetchErr => {
                console.error('Error fetching STL file:', fetchErr);
                handleApiError(fetchErr, 'exportSubtitles', transcriptStatus.data.recordId, apiClient);
            });
    } else {
        saveFileContent(res.data, fileName, fileExtension, 'application/octet-stream');
    }
}

export function exportSubtitles(
    transcript,
    transcriptStatus,
    options,
    subtitleFormat,
    isTranscriber,
    isTranscription,
    recordIdOrJobId
) {
    const { subtitleDurationMax, maxNumberOfRows, maxCharsPerRow } = options;
    const formatForApi = subtitleFormat === 'EBU-STL' ? 'stl' : subtitleFormat.toLowerCase();
    const URL = `/export/${transcriptStatus.data.job.id}/${formatForApi}`;
   
    return apiClient
        .get(URL, {
            params: {
                jobId: transcriptStatus.data.job.id,
                maxCharsPerRow,
                maxNumberOfRows,
                maxScreenTimePerRowSeconds: subtitleDurationMax,
            },
        })
        .then((res) => {
            if (subtitleFormat === 'EBU-STL') {
                return handleStlFormat(res, subtitleFormat, isTranscriber, isTranscription, recordIdOrJobId, transcriptStatus);
            }
            
            const fileName = getFileName(isTranscriber, isTranscription, recordIdOrJobId, transcriptStatus);
            const fileExtension = getFileExtension(subtitleFormat);
            saveFileContent(res.data, fileName, fileExtension);
        })
        .catch((err) => {
            handleApiError(err, 'exportSubtitles', transcriptStatus.data.recordId, apiClient);
            if (subtitleFormat === 'srt') {
                exportSrt(
                    transcript.data.segments.valueSeq().toArray(),
                    transcriptStatus,
                    subtitleDurationMax,
                );
            } else if (subtitleFormat === 'vtt') {
                exportVtt(
                    transcript.data.segments.valueSeq().toArray(),
                    transcriptStatus,
                    subtitleDurationMax,
                );
            } else if (subtitleFormat === 'stl') {
                exportStl(transcript, transcriptStatus, subtitleDurationMax);
            }
        });
}

// ********* MEDIA ********* \\
export function exportMedia(transcriptStatus) {
    const { recordId } = transcriptStatus.data;
    apiClient.get(`userrecord/${recordId}/original-media-link`).then(res => {
        window.open(res.data.downloadUrl, "_blank");
    });
}

// ********* TRACKING ********* \\
export function trackExport(format, transcriptStatus) {
    apiClient.post('analytics/trackEvent', {}, {
        params: {
            type: 'export',
            recordId: transcriptStatus.data.recordId,
            format
        }
    });
}

export async function getOriginalText(jobId) {
    const URL = `/document/transcription/${jobId}`;
    try {
        const response = await apiClient.get(URL);
        return response.data;
    } catch (error) {
        console.error('Error in getOriginalText:', error);
    }
}

// Re-export getExportFilename
export { getExportFilename };